/**
 * Jetson Bridge Service
 * Provides communication layer between web app and Jetson devices
 * Includes simulated implementation using Web Speech API for development
 */

import type {
  JetsonDevice,
  JetsonCommand,
  JetsonMessage,
  TranscriptionResult,
  VoiceMenuItem
} from '../types/voiceOrdering';

// Web Speech API type declarations
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

/**
 * Jetson Bridge Interface
 * Defines the contract for Jetson device communication
 */
export interface IJetsonBridge {
  connect(device: JetsonDevice): Promise<boolean>;
  disconnect(): Promise<void>;
  isConnected(): boolean;
  getDevice(): JetsonDevice | null;

  // Audio processing
  sendAudio(audioData: ArrayBuffer): Promise<TranscriptionResult>;
  requestTTS(text: string): Promise<ArrayBuffer>;

  // Menu sync
  syncMenu(menuItems: VoiceMenuItem[]): Promise<void>;

  // Device status
  getStatus(): Promise<{ status: string; lastSeen: string }>;

  // Event handlers
  onTranscription(callback: (result: TranscriptionResult) => void): void;
  onMessage(callback: (message: JetsonMessage) => void): void;
  onError(callback: (error: Error) => void): void;
}

/**
 * Simulated Jetson Bridge
 * Uses Web Speech API for development/testing without actual Jetson hardware
 */
export class SimulatedJetsonBridge implements IJetsonBridge {
  private connected = false;
  private device: JetsonDevice | null = null;
  private recognition: SpeechRecognition | null = null;
  private synthesis: SpeechSynthesis | null = null;
  private transcriptionCallback?: (result: TranscriptionResult) => void;
  private messageCallback?: (message: JetsonMessage) => void;
  private errorCallback?: (error: Error) => void;
  private isListening = false;

  constructor() {
    // Initialize Web Speech API if available
    if (typeof window !== 'undefined') {
      const SpeechRecognitionAPI = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognitionAPI) {
        this.recognition = new SpeechRecognitionAPI();
        this.setupRecognition();
      }
      this.synthesis = window.speechSynthesis;
    }
  }

  private setupRecognition(): void {
    if (!this.recognition) return;

    this.recognition.continuous = false;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      const result = event.results[event.results.length - 1];
      const transcript = result[0].transcript;
      const confidence = result[0].confidence;
      const isFinal = result.isFinal;

      const transcriptionResult: TranscriptionResult = {
        text: transcript,
        confidence: confidence || 0.9,
        isFinal,
        alternatives: Array.from(result).slice(1).map(alt => alt.transcript)
      };

      this.transcriptionCallback?.(transcriptionResult);

      if (isFinal) {
        this.messageCallback?.({
          type: 'transcription',
          payload: transcriptionResult,
          timestamp: new Date().toISOString()
        });
      }
    };

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const error = new Error(`Speech recognition error: ${event.error}`);
      this.errorCallback?.(error);
      this.messageCallback?.({
        type: 'error',
        payload: { error: event.error, message: event.message },
        timestamp: new Date().toISOString()
      });
    };

    this.recognition.onend = () => {
      this.isListening = false;
      // Auto-restart if still supposed to be listening
      if (this.connected && this.recognition) {
        // Don't auto-restart - let the caller control this
      }
    };
  }

  async connect(device: JetsonDevice): Promise<boolean> {
    // Simulate connection delay
    await new Promise(resolve => setTimeout(resolve, 500));

    this.device = {
      ...device,
      deviceId: device.deviceId || 'simulated-jetson',
      status: 'online'
    };
    this.connected = true;

    this.messageCallback?.({
      type: 'status',
      payload: { status: 'connected', device: this.device },
      timestamp: new Date().toISOString()
    });

    return true;
  }

  async disconnect(): Promise<void> {
    this.stopListening();
    this.connected = false;
    this.device = null;

    this.messageCallback?.({
      type: 'status',
      payload: { status: 'disconnected' },
      timestamp: new Date().toISOString()
    });
  }

  isConnected(): boolean {
    return this.connected;
  }

  getDevice(): JetsonDevice | null {
    return this.device;
  }

  /**
   * Start listening for speech
   */
  startListening(): void {
    if (!this.recognition || this.isListening) return;

    try {
      this.recognition.start();
      this.isListening = true;
    } catch (error) {
      // Already started or not supported
      console.warn('Could not start speech recognition:', error);
    }
  }

  /**
   * Stop listening for speech
   */
  stopListening(): void {
    if (!this.recognition || !this.isListening) return;

    try {
      this.recognition.stop();
      this.isListening = false;
    } catch (error) {
      console.warn('Could not stop speech recognition:', error);
    }
  }

  /**
   * Check if currently listening
   */
  getIsListening(): boolean {
    return this.isListening;
  }

  async sendAudio(audioData: ArrayBuffer): Promise<TranscriptionResult> {
    // In simulation, we use Web Speech API directly
    // This method is here for API compatibility with real Jetson
    return {
      text: '',
      confidence: 0,
      isFinal: false,
      alternatives: []
    };
  }

  async requestTTS(text: string): Promise<ArrayBuffer> {
    // Use Web Speech Synthesis
    if (this.synthesis) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      // Find a good voice
      const voices = this.synthesis.getVoices();
      const preferredVoice = voices.find(v =>
        v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')
      ) || voices[0];

      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      return new Promise((resolve, reject) => {
        utterance.onend = () => {
          this.messageCallback?.({
            type: 'audio_complete',
            payload: { text },
            timestamp: new Date().toISOString()
          });
          // Return empty buffer since we played audio directly
          resolve(new ArrayBuffer(0));
        };

        utterance.onerror = (event) => {
          reject(new Error(`TTS error: ${event.error}`));
        };

        this.synthesis?.speak(utterance);
      });
    }

    throw new Error('Speech synthesis not available');
  }

  /**
   * Speak text using TTS (convenience method)
   */
  speak(text: string): void {
    if (!this.synthesis) {
      console.warn('Speech synthesis not available');
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    const voices = this.synthesis.getVoices();
    const preferredVoice = voices.find(v =>
      v.name.includes('Google') || v.name.includes('Samantha')
    ) || voices[0];

    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    this.synthesis.speak(utterance);
  }

  async syncMenu(menuItems: VoiceMenuItem[]): Promise<void> {
    // Simulate menu sync
    await new Promise(resolve => setTimeout(resolve, 1000));

    this.messageCallback?.({
      type: 'sync_complete',
      payload: { itemCount: menuItems.length },
      timestamp: new Date().toISOString()
    });
  }

  async getStatus(): Promise<{ status: string; lastSeen: string }> {
    return {
      status: this.connected ? 'online' : 'offline',
      lastSeen: new Date().toISOString()
    };
  }

  onTranscription(callback: (result: TranscriptionResult) => void): void {
    this.transcriptionCallback = callback;
  }

  onMessage(callback: (message: JetsonMessage) => void): void {
    this.messageCallback = callback;
  }

  onError(callback: (error: Error) => void): void {
    this.errorCallback = callback;
  }
}

/**
 * Real Jetson Bridge
 * WebSocket-based communication with actual Jetson device
 */
export class RealJetsonBridge implements IJetsonBridge {
  private socket: WebSocket | null = null;
  private device: JetsonDevice | null = null;
  private transcriptionCallback?: (result: TranscriptionResult) => void;
  private messageCallback?: (message: JetsonMessage) => void;
  private errorCallback?: (error: Error) => void;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;

  async connect(device: JetsonDevice): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        const wsUrl = `ws://${device.ipAddress}:${device.port}/ws`;
        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          this.device = { ...device, status: 'online' };
          this.reconnectAttempts = 0;

          this.messageCallback?.({
            type: 'status',
            payload: { status: 'connected', device: this.device },
            timestamp: new Date().toISOString()
          });

          resolve(true);
        };

        this.socket.onmessage = (event) => {
          try {
            const message: JetsonMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse Jetson message:', error);
          }
        };

        this.socket.onerror = (event) => {
          const error = new Error('WebSocket connection error');
          this.errorCallback?.(error);
          reject(error);
        };

        this.socket.onclose = () => {
          this.handleDisconnect();
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  private handleMessage(message: JetsonMessage): void {
    this.messageCallback?.(message);

    if (message.type === 'transcription') {
      this.transcriptionCallback?.(message.payload as TranscriptionResult);
    }
  }

  private handleDisconnect(): void {
    if (this.device) {
      this.device.status = 'offline';
    }

    this.messageCallback?.({
      type: 'status',
      payload: { status: 'disconnected' },
      timestamp: new Date().toISOString()
    });

    // Attempt reconnection
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.device) {
      this.reconnectAttempts++;
      setTimeout(() => {
        if (this.device) {
          this.connect(this.device).catch(console.error);
        }
      }, 2000 * this.reconnectAttempts);
    }
  }

  async disconnect(): Promise<void> {
    this.maxReconnectAttempts = 0; // Prevent reconnection
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.device = null;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getDevice(): JetsonDevice | null {
    return this.device;
  }

  async sendAudio(audioData: ArrayBuffer): Promise<TranscriptionResult> {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new Error('Not connected to Jetson device'));
        return;
      }

      // Send audio data
      this.socket.send(audioData);

      // Wait for transcription response
      const timeout = setTimeout(() => {
        reject(new Error('Transcription timeout'));
      }, 10000);

      const handler = (result: TranscriptionResult) => {
        if (result.isFinal) {
          clearTimeout(timeout);
          this.transcriptionCallback = undefined;
          resolve(result);
        }
      };

      this.transcriptionCallback = handler;
    });
  }

  async requestTTS(text: string): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new Error('Not connected to Jetson device'));
        return;
      }

      const command: JetsonCommand = {
        type: 'play_tts',
        payload: { text },
        timestamp: new Date().toISOString()
      };

      this.socket.send(JSON.stringify(command));

      // For TTS, we might receive audio data back or just a confirmation
      // This depends on the Jetson implementation
      const timeout = setTimeout(() => {
        reject(new Error('TTS timeout'));
      }, 10000);

      // Listen for audio_complete message
      const originalHandler = this.messageCallback;
      this.messageCallback = (message) => {
        originalHandler?.(message);
        if (message.type === 'audio_complete') {
          clearTimeout(timeout);
          this.messageCallback = originalHandler;
          resolve(message.payload?.audioData || new ArrayBuffer(0));
        }
      };
    });
  }

  async syncMenu(menuItems: VoiceMenuItem[]): Promise<void> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected to Jetson device');
    }

    const command: JetsonCommand = {
      type: 'sync_menu',
      payload: { menuItems },
      timestamp: new Date().toISOString()
    };

    this.socket.send(JSON.stringify(command));

    // Wait for sync_complete
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Menu sync timeout'));
      }, 30000);

      const originalHandler = this.messageCallback;
      this.messageCallback = (message) => {
        originalHandler?.(message);
        if (message.type === 'sync_complete') {
          clearTimeout(timeout);
          this.messageCallback = originalHandler;
          resolve();
        }
      };
    });
  }

  async getStatus(): Promise<{ status: string; lastSeen: string }> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return {
        status: 'offline',
        lastSeen: this.device?.lastSeen || ''
      };
    }

    const command: JetsonCommand = {
      type: 'get_status',
      timestamp: new Date().toISOString()
    };

    this.socket.send(JSON.stringify(command));

    return {
      status: 'online',
      lastSeen: new Date().toISOString()
    };
  }

  onTranscription(callback: (result: TranscriptionResult) => void): void {
    this.transcriptionCallback = callback;
  }

  onMessage(callback: (message: JetsonMessage) => void): void {
    this.messageCallback = callback;
  }

  onError(callback: (error: Error) => void): void {
    this.errorCallback = callback;
  }
}

/**
 * Jetson Bridge Factory
 * Creates the appropriate bridge based on environment/availability
 */
export function createJetsonBridge(options?: {
  forceSimulation?: boolean;
  device?: JetsonDevice;
}): IJetsonBridge {
  // Use simulation if forced or no device specified
  if (options?.forceSimulation || !options?.device) {
    return new SimulatedJetsonBridge();
  }

  // Try to use real bridge
  return new RealJetsonBridge();
}

/**
 * Check if Web Speech API is available
 */
export function isWebSpeechAvailable(): { recognition: boolean; synthesis: boolean } {
  if (typeof window === 'undefined') {
    return { recognition: false, synthesis: false };
  }

  const SpeechRecognitionAPI = window.SpeechRecognition || (window as any).webkitSpeechRecognition;

  return {
    recognition: !!SpeechRecognitionAPI,
    synthesis: !!window.speechSynthesis
  };
}

export default createJetsonBridge;
